import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import { isUuid } from '@/lib/uuid';
import { usePersistedSearchParams } from '@/preferences';
import { fetchBackofficeProjects, fetchBackofficeUserByEmail } from '../api';
import type { BackofficeProjectRow, ProjectSortBy, ProjectStatus, SortDirection } from '../types';
import { ProjectsFilters } from './ProjectsFilters';
import { ProjectsTable } from './ProjectsTable';

const DEFAULT_LIMIT = 20;
const SORTABLE_FIELDS = new Set<ProjectSortBy>(['title', 'status', 'updatedAt', 'createdAt']);
const STATUS_VALUES = new Set<ProjectStatus>(['draft', 'in_progress', 'completed', 'archived']);
export interface ProjectsListSectionProps {
  fixedOrganizationId?: string;
  fixedOwnerId?: string;
  showOrganizationFilter?: boolean;
  showOrganizationColumn?: boolean;
  showOwnerFilter?: boolean;
  showOwnerColumn?: boolean;
  showAccessFilter?: boolean;
  preferenceScope?: string;
  surface?: 'page' | 'tile' | undefined;
}

function readSortBy(value: string | null): ProjectSortBy {
  return value && SORTABLE_FIELDS.has(value as ProjectSortBy)
    ? (value as ProjectSortBy)
    : 'updatedAt';
}

function readSortDirection(value: string | null): SortDirection {
  return value === 'asc' || value === 'desc' ? value : 'desc';
}

function readStatus(value: string | null): ProjectStatus | '' {
  return value && STATUS_VALUES.has(value as ProjectStatus) ? (value as ProjectStatus) : '';
}

export function ProjectsListSection({
  fixedOrganizationId,
  fixedOwnerId,
  showOrganizationFilter = true,
  showOrganizationColumn = true,
  showOwnerFilter = true,
  showOwnerColumn = true,
  showAccessFilter = false,
  preferenceScope = 'list:projects',
  surface = 'page',
}: ProjectsListSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const persistedSearchParamKeys = useMemo(
    () =>
      [
        'status',
        fixedOrganizationId ? null : 'organizationId',
        fixedOwnerId ? null : 'ownerUserId',
        'updatedFrom',
        'updatedTo',
        showAccessFilter ? 'includeAllWithAccess' : null,
        'sortBy',
        'sortDirection',
      ].filter((key): key is string => Boolean(key)),
    [fixedOrganizationId, fixedOwnerId, showAccessFilter]
  );
  const [projects, setProjects] = useState<BackofficeProjectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const status = readStatus(searchParams.get('status'));
  const organizationId = fixedOrganizationId ?? searchParams.get('organizationId') ?? '';
  const owner = fixedOwnerId ?? searchParams.get('ownerUserId') ?? '';
  const requestOrganizationId = isUuid(organizationId) ? organizationId : '';
  const updatedFrom = searchParams.get('updatedFrom') ?? '';
  const updatedTo = searchParams.get('updatedTo') ?? '';
  const includeAllWithAccess =
    showAccessFilter && searchParams.get('includeAllWithAccess') === 'true';
  const sortBy = readSortBy(searchParams.get('sortBy'));
  const sortDirection = readSortDirection(searchParams.get('sortDirection'));

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (status) params.set('status', status);
    if (requestOrganizationId) params.set('organizationId', requestOrganizationId);
    if (updatedFrom) params.set('updatedFrom', updatedFrom);
    if (updatedTo) params.set('updatedTo', updatedTo);
    if (includeAllWithAccess) params.set('includeAllWithAccess', 'true');
    return params;
  }, [
    sortBy,
    sortDirection,
    status,
    requestOrganizationId,
    updatedFrom,
    updatedTo,
    includeAllWithAccess,
  ]);

  const hasMore = projects.length < total;

  const loadProjects = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      const trimmedOwner = owner.trim();
      if (trimmedOwner) {
        if (isUuid(trimmedOwner)) {
          params.set('ownerUserId', trimmedOwner);
        } else {
          const lookup = await fetchBackofficeUserByEmail(trimmedOwner, signal);
          if (!lookup) {
            setTotal(0);
            setProjects((current) => (offset === 0 ? [] : current));
            return;
          }
          params.set('ownerUserId', lookup.user.id);
        }
      }
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await fetchBackofficeProjects(params, signal);
      setTotal(response.page.total);
      setProjects((current) =>
        offset === 0 ? response.projects : [...current, ...response.projects]
      );
    },
    [owner, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadProjects(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setProjects([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh studies');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadProjects]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadProjects(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setProjects([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load studies');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadProjects]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadProjects(projects.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more studies'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadProjects, projects.length]);

  usePersistedSearchParams({
    scope: preferenceScope,
    keys: persistedSearchParamKeys,
    searchParams,
    setSearchParams,
  });

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }

  function handleSortChange(nextSortBy: ProjectSortBy) {
    const nextDirection = sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
    updateParams({ sortBy: nextSortBy, sortDirection: nextDirection });
  }

  return (
    <section
      className={
        surface === 'tile'
          ? 'flex h-full min-h-full flex-col overflow-hidden bg-white'
          : 'rounded-xl border border-neutral-200 bg-white'
      }
    >
      <ProjectsFilters
        status={status}
        organizationId={organizationId}
        owner={owner}
        updatedFrom={updatedFrom}
        updatedTo={updatedTo}
        includeAllWithAccess={includeAllWithAccess}
        actions={tableActions}
        showOrganizationFilter={showOrganizationFilter}
        showOwnerFilter={showOwnerFilter}
        showAccessFilter={showAccessFilter}
        onStatusChange={(nextStatus) => updateParams({ status: nextStatus })}
        onOrganizationIdChange={(value) => updateParams({ organizationId: value })}
        onOwnerChange={(value) => updateParams({ ownerUserId: value })}
        onUpdatedRangeChange={(from, to) => updateParams({ updatedFrom: from, updatedTo: to })}
        onIncludeAllWithAccessChange={(value) =>
          updateParams({ includeAllWithAccess: value ? 'true' : null })
        }
      />

      {initialLoading ? (
        <div className="px-5 py-16 text-center text-body text-neutral-500">Loading studies</div>
      ) : error ? (
        <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
      ) : projects.length > 0 ? (
        <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <ProjectsTable
            projects={projects}
            sortBy={sortBy}
            sortDirection={sortDirection}
            showOrganizationColumn={showOrganizationColumn}
            showOwnerColumn={showOwnerColumn}
            onSortChange={handleSortChange}
          />
          <div ref={loadMoreRef} className="border-t border-neutral-200 px-5 py-4 text-center">
            {loadingMore ? (
              <span className="text-small text-neutral-500">Loading more studies</span>
            ) : loadMoreError ? (
              <span className="text-small text-red-700">{loadMoreError}</span>
            ) : hasMore ? (
              <span className="text-small text-neutral-500">Scroll to load more</span>
            ) : (
              <span className="text-small text-neutral-500">
                Showing {projects.length} of {total}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="px-5 py-16 text-center text-body text-neutral-500">No studies found</div>
      )}
    </section>
  );
}
